import { Link } from 'react-router';
import { moedaCompacta, plural } from '../util/formato';

export interface FaseDoFunil {
  readonly fase: string;
  readonly rotulo: string;
  readonly cor: string;
  readonly quantidade: number;
  readonly valor: number;
}

/**
 * Funil de cobrança.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUE NÃO SÃO ONZE CARTÕES IGUAIS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A versão anterior desenhava um cartão por fase, todos do mesmo tamanho. O
 * resultado era um paredão em que só dava para saber onde a carteira está parada
 * LENDO os onze números e comparando de cabeça.
 *
 * Aqui a largura da barra é proporcional à quantidade. O acúmulo aparece antes de
 * qualquer número ser lido — que é o que um funil existe para mostrar. Os números
 * seguem ali para quem precisa do valor exato.
 */
export function Funil({ fases }: { fases: readonly FaseDoFunil[] }) {
  const comCasos = fases.filter((f) => f.quantidade > 0);
  const vazias = fases.filter((f) => f.quantidade === 0);
  const total = comCasos.reduce((soma, f) => soma + f.quantidade, 0);

  if (total === 0) {
    return (
      <p className="py-8 text-center text-sm text-[color:var(--texto-tenue)]">
        Nenhum devedor na carteira ainda.
      </p>
    );
  }

  const maior = Math.max(...comCasos.map((f) => f.quantidade));

  return (
    <div className="flex flex-col gap-1">
      {comCasos.map((fase) => {
        const proporcao = (fase.quantidade / maior) * 100;
        const fatia = (fase.quantidade / total) * 100;

        return (
          <Link
            key={fase.fase}
            to={`/devedores?fase=${fase.fase}`}
            className="group grid grid-cols-[minmax(0,15rem)_1fr_auto] items-center gap-4 rounded px-2 py-1.5 transition-colors hover:bg-[color:var(--superficie-afundada)]"
          >
            <span className="flex items-center gap-2 truncate text-sm text-[color:var(--texto)]">
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full"
                style={{ background: fase.cor }}
              />
              <span className="truncate">{fase.rotulo}</span>
            </span>

            <span className="relative h-6 overflow-hidden rounded-sm bg-[color:var(--superficie-afundada)]">
              <span
                className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-500"
                style={{
                  width: `${proporcao}%`,
                  // 22% de opacidade sustenta a leitura da proporção sem competir
                  // com o texto que fica por cima.
                  background: `color-mix(in srgb, ${fase.cor} 22%, transparent)`,
                  borderLeft: `2px solid ${fase.cor}`,
                }}
              />
              <span className="tabular relative z-10 flex h-full items-center px-2.5 text-xs font-medium text-[color:var(--texto-suave)]">
                {plural(fase.quantidade, 'caso', 'casos')}
                <span className="ml-2 text-[color:var(--texto-tenue)]">
                  {fatia.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                </span>
              </span>
            </span>

            <span className="tabular w-24 text-right text-sm text-[color:var(--texto-suave)]">
              {moedaCompacta(fase.valor)}
            </span>
          </Link>
        );
      })}

      {/* ═══════════════════════════════════════════════════════════════════
          AS FASES VAZIAS, NUMA LINHA SÓ
          ═══════════════════════════════════════════════════════════════════

          Barra de comprimento zero não diz nada sobre proporção, e uma fase por
          linha com "0 casos" transformaria o funil num paredão em que o acúmulo —
          a única coisa que um funil existe para mostrar — se perde no meio.

          Some-las por completo, porém, faz parecer que a fase não existe: foi o
          que aconteceu com as cinco novas, que nasceram vazias e não apareciam em
          lugar nenhum do painel. Aqui elas ficam nomeadas, e clicáveis. */}
      {vazias.length > 0 && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-1 border-t px-2 pt-3 text-xs text-[color:var(--texto-tenue)]">
          <span>Sem nenhum caso agora:</span>
          {vazias.map((fase, i) => (
            <span key={fase.fase}>
              <Link
                to={`/devedores?fase=${fase.fase}`}
                className="hover:text-[color:var(--acento)]"
              >
                {fase.rotulo}
              </Link>
              {i < vazias.length - 1 && <span className="ml-1.5">·</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
